import React, { useEffect, useRef, useState } from 'react';
import Container from '../../components/LandingPage/Container';
import { Box, Button, TextField, Typography } from '@mui/material';
import { AutoAwesomeOutlined, SendOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ReactMarkdown from 'react-markdown';
import API_CONFIG from '../../config/api';
import {
  getCampaignChatHistory,
  ResellerCampaignChatMessage,
} from '../../services/campaign-service';

interface Message {
  message: string;
  isUser: boolean;
}

const CHAT_STORAGE_KEY = 'campaign_chat_id';
const CHAT_BY_IDENTIFIER_STORAGE_KEY = 'campaign_chat_ids_by_identifier';

interface StoredCampaignChat {
  identifier: string;
  chat_id: string;
}

const TypingIndicator: React.FC = () => {
  return (
    <Box width="100%" display="flex" justifyContent="flex-start">
      <Box
        bgcolor="var(--acer-lightgray)"
        padding="12px 16px"
        display="flex"
        alignItems="center"
        gap="4px"
      >
        {[0, 1, 2].map(dot => (
          <Box
            key={dot}
            width="8px"
            height="8px"
            borderRadius="50%"
            bgcolor="var(--acer-mediumgray)"
            sx={{
              animation: 'chat-typing-blink 1.2s infinite ease-in-out',
              animationDelay: `${dot * 0.2}s`,
              '@keyframes chat-typing-blink': {
                '0%, 80%, 100%': {
                  opacity: 0.25,
                  transform: 'translateY(0)',
                },
                '40%': {
                  opacity: 1,
                  transform: 'translateY(-2px)',
                },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const Message: React.FC<{
  message: Message;
}> = ({ message }) => {
  return (
    <Box width="100%" display="flex" justifyContent={message.isUser ? 'flex-end' : 'flex-start'}>
      <Box
        maxWidth="80%"
        width="fit-content"
        bgcolor={message.isUser ? 'var(--acer-green)' : 'var(--acer-lightgray)'}
        padding="12px 16px"
        display="flex"
        justifyContent={message.isUser ? 'flex-end' : 'flex-start'}
      >
        {message.isUser ? (
          <Typography color="white" fontSize="14px" lineHeight="1.625">
            {message.message}
          </Typography>
        ) : (
          <Box
            color="var(--acer-dark)"
            fontSize="14px"
            lineHeight="1.625"
            sx={{
              '& p': { margin: 0 },
              '& p + p': { marginTop: '8px' },
              '& ul, & ol': { margin: '8px 0', paddingLeft: '20px' },
              '& code': {
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                padding: '2px 4px',
                borderRadius: '4px',
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                padding: '8px',
                borderRadius: '6px',
                overflowX: 'auto',
              },
              '& pre code': {
                backgroundColor: 'transparent',
                padding: 0,
              },
            }}
          >
            <ReactMarkdown>{message.message}</ReactMarkdown>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const Chat: React.FC = () => {
  const { identifier: templateIdentifier } = useParams<{
    identifier?: string;
  }>();
  const { enqueueSnackbar } = useSnackbar();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [chatId, setChatId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isAssistantTyping, setIsAssistantTyping] = useState<boolean>(false);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleNewMessage = (message: string) => {
    setNewMessage(message);
  };

  const isUserRole = (role: string): boolean => {
    const normalizedRole = role.toLowerCase();
    return normalizedRole.includes('user') || normalizedRole.includes('human');
  };

  const getStoredChatsByIdentifier = (): StoredCampaignChat[] => {
    try {
      const rawValue = localStorage.getItem(CHAT_BY_IDENTIFIER_STORAGE_KEY);
      if (!rawValue) {
        return [];
      }

      const parsed = JSON.parse(rawValue) as unknown;
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed.filter(
        (item): item is StoredCampaignChat =>
          typeof item === 'object' &&
          item !== null &&
          typeof (item as StoredCampaignChat).identifier === 'string' &&
          typeof (item as StoredCampaignChat).chat_id === 'string'
      );
    } catch {
      return [];
    }
  };

  const saveChatIdWithIdentifier = (newChatId: string, identifier?: string) => {
    localStorage.setItem(CHAT_STORAGE_KEY, newChatId);

    if (!identifier) {
      return;
    }

    const storedChats = getStoredChatsByIdentifier();
    const existingIndex = storedChats.findIndex(chat => chat.identifier === identifier);

    if (existingIndex >= 0) {
      storedChats[existingIndex] = { identifier, chat_id: newChatId };
    } else {
      storedChats.push({ identifier, chat_id: newChatId });
    }

    localStorage.setItem(CHAT_BY_IDENTIFIER_STORAGE_KEY, JSON.stringify(storedChats));
  };

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    inputRef.current?.focus();
  }, [messages, isAssistantTyping]);

  useEffect(() => {
    let isMounted = true;
    const storedChatsByIdentifier = getStoredChatsByIdentifier();
    const storedChatId =
      templateIdentifier && templateIdentifier.trim()
        ? (storedChatsByIdentifier.find(chat => chat.identifier === templateIdentifier)?.chat_id ??
          null)
        : localStorage.getItem(CHAT_STORAGE_KEY);

    if (!storedChatId) {
      setChatId(null);
      setMessages([]);
      return () => {
        isMounted = false;
      };
    }

    const loadChatHistory = async () => {
      setChatId(storedChatId);
      setIsAssistantTyping(false);

      const history = await getCampaignChatHistory(storedChatId);
      if (!isMounted) {
        return;
      }

      if (!history) {
        if (templateIdentifier && templateIdentifier.trim()) {
          const filteredChats = storedChatsByIdentifier.filter(
            chat => chat.identifier !== templateIdentifier
          );
          localStorage.setItem(CHAT_BY_IDENTIFIER_STORAGE_KEY, JSON.stringify(filteredChats));
        } else {
          localStorage.removeItem(CHAT_STORAGE_KEY);
        }

        setChatId(null);
        setMessages([]);
        enqueueSnackbar('Não foi possível carregar o histórico do chat.', { variant: 'error' });
        return;
      }

      const sortedHistory = [...history].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );

      const mappedMessages: Message[] = sortedHistory.map(
        (historyMessage: ResellerCampaignChatMessage) => ({
          message: historyMessage.message,
          isUser: isUserRole(historyMessage.role),
        })
      );

      setMessages(mappedMessages);
    };

    loadChatHistory();

    return () => {
      isMounted = false;
    };
  }, [templateIdentifier, enqueueSnackbar]);

  const isDoneMarker = (value: string): boolean => value.trim().toUpperCase() === '[DONE]';

  const takeNextSseBlock = (source: string): { block: string; rest: string } | null => {
    const separatorMatch = source.match(/\r?\n\r?\n/);
    if (!separatorMatch || separatorMatch.index === undefined) {
      return null;
    }

    const separatorIndex = separatorMatch.index;
    const separatorLength = separatorMatch[0].length;

    return {
      block: source.slice(0, separatorIndex),
      rest: source.slice(separatorIndex + separatorLength),
    };
  };

  const parseSseBlock = (rawBlock: string): { event: string; data: string } | null => {
    if (!rawBlock) {
      return null;
    }

    const lines = rawBlock.split(/\r?\n/);
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith(':')) {
        continue;
      }

      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim() || 'message';
        continue;
      }

      if (line.startsWith('data:')) {
        // Mantém conteúdo original (inclusive espaços à direita).
        dataLines.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
        continue;
      }

      // Backend pode enviar quebras dentro do payload sem repetir "data:".
      dataLines.push(line);
    }

    if (!dataLines.length) {
      return null;
    }

    return {
      event: eventName,
      data: dataLines.join('\n'),
    };
  };

  const getErrorMessage = async (response: Response): Promise<string> => {
    try {
      const json = (await response.json()) as { detail?: unknown; message?: string };
      if (typeof json.message === 'string' && json.message.trim()) {
        return json.message;
      }
      if (typeof json.detail === 'string' && json.detail.trim()) {
        return json.detail;
      }
    } catch {
      // Ignora parse de erro e usa fallback
    }

    return 'Não foi possível enviar sua mensagem. Tente novamente.';
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    const userMessageText = newMessage.trim();

    const message: Message = { message: userMessageText, isUser: true };
    setMessages(prev => [...prev, message]);
    setNewMessage('');
    setIsSending(true);
    setIsAssistantTyping(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_CONFIG.BASE_URL_WITH_VERSION}/campaigns/chats/send-message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream, application/x-ndjson, application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message: userMessageText,
            chat_id: chatId,
            identifier: templateIdentifier ?? null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      console.log('[SSE content-type]', response.headers.get('content-type'));

      if (!response.body) {
        throw new Error('Resposta sem corpo de stream.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let assistantReply = '';
      let shouldContinue = true;
      let hasReceivedAssistantText = false;

      const updateAssistantMessage = (value: string) => {
        setMessages(prev => {
          const lastIndex = prev.length - 1;
          const lastMessage = prev[lastIndex];

          if (lastMessage && !lastMessage.isUser) {
            const updated = [...prev];
            updated[lastIndex] = { ...lastMessage, message: value };
            return updated;
          }

          return [...prev, { message: value, isUser: false }];
        });
      };

      const handleDispatchedEvent = (eventName: string, data: string): boolean => {
        if (eventName === 'chat_id') {
          const parsedChatId = data.trim();
          if (parsedChatId) {
            setChatId(parsedChatId);
            saveChatIdWithIdentifier(parsedChatId, templateIdentifier);
          }
          return false;
        }

        if (eventName === 'done' || isDoneMarker(data)) {
          setIsAssistantTyping(false);
          return true;
        }

        if (data.length > 0) {
          setIsAssistantTyping(false);
        }

        assistantReply += data;
        hasReceivedAssistantText = hasReceivedAssistantText || data.length > 0;
        updateAssistantMessage(assistantReply);
        return false;
      };

      while (shouldContinue) {
        const { value, done } = await reader.read();
        if (done) {
          shouldContinue = false;
          continue;
        }

        if (!value) {
          continue;
        }

        const decodedChunk = decoder.decode(value, { stream: true });
        console.log('[SSE chunk raw]', JSON.stringify(decodedChunk));
        buffer += decodedChunk;
        let nextBlock = takeNextSseBlock(buffer);

        while (nextBlock) {
          if (nextBlock.block === '') {
            if (hasReceivedAssistantText) {
              assistantReply += '\n';
              updateAssistantMessage(assistantReply);
            }
            buffer = nextBlock.rest;
            nextBlock = takeNextSseBlock(buffer);
            continue;
          }

          const parsedEvent = parseSseBlock(nextBlock.block);
          buffer = nextBlock.rest;

          if (parsedEvent) {
            if (parsedEvent.event === 'message' && parsedEvent.data === '') {
              if (hasReceivedAssistantText) {
                assistantReply += '\n';
                updateAssistantMessage(assistantReply);
              }
              nextBlock = takeNextSseBlock(buffer);
              continue;
            }

            const shouldStop = handleDispatchedEvent(parsedEvent.event, parsedEvent.data);
            if (shouldStop) {
              shouldContinue = false;
              break;
            }
          }

          nextBlock = takeNextSseBlock(buffer);
        }
      }

      const finalChunk = decoder.decode();
      if (finalChunk) {
        buffer += finalChunk;
      }

      if (buffer.trim()) {
        const parsedEvent = parseSseBlock(buffer);
        if (parsedEvent) {
          if (parsedEvent.event === 'message' && parsedEvent.data === '') {
            if (hasReceivedAssistantText) {
              assistantReply += '\n';
              updateAssistantMessage(assistantReply);
            }
          } else {
            handleDispatchedEvent(parsedEvent.event, parsedEvent.data);
          }
        }
      }

      console.log('[Assistant markdown input]', JSON.stringify(assistantReply));
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim()
          ? error.message
          : 'Não foi possível enviar sua mensagem. Tente novamente.';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setIsSending(false);
      setIsAssistantTyping(false);
    }
  };

  return (
    <Container>
      <Box padding="54px 0" width="100%" height="100vh" minHeight="600px" id="chat">
        <Box
          boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.1)"
          width="100%"
          height="100%"
          sx={{
            '&:focus-within': {
              boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1) !important',
            },
          }}
        >
          {/* Header */}
          <Box
            width="100%"
            height="54px"
            bgcolor="var(--acer-dark)"
            padding="12px 16px"
            alignContent="center"
          >
            <Typography
              color="white"
              fontSize="16px"
              fontWeight="600"
              display="flex"
              alignItems="center"
              gap="8px"
            >
              <AutoAwesomeOutlined sx={{ color: 'var(--acer-green)' }} />
              Assistente de Revendas Acer
            </Typography>
          </Box>
          <Box
            height="calc(100% - 54px)"
            display="flex"
            flexDirection="column"
            justifyContent="end"
            padding="12px 16px"
            gap="16px"
          >
            {/* Messages */}
            <Box
              ref={messagesContainerRef}
              display="flex"
              flexDirection="column"
              gap="16px"
              overflow="auto"
              padding="0 20px"
            >
              {messages.map((message, index) => (
                <Message key={index} message={message} />
              ))}
              {isAssistantTyping && <TypingIndicator />}
            </Box>
            {/* Input */}
            <Box display="flex" alignItems="center" gap="16px" width="100%">
              <Box
                border="1px solid var(--acer-lightgray)"
                borderRadius="28px"
                padding="0 12px"
                width="100%"
              >
                <TextField
                  inputRef={inputRef}
                  placeholder="Digite sua mensagem"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiOutlinedInput-input:focus': {
                      backgroundColor: 'transparent',
                    },
                  }}
                  value={newMessage}
                  onChange={e => handleNewMessage(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSendMessage();
                    }
                  }}
                />
              </Box>
              <Box height="100%">
                <Button
                  disabled={isSending}
                  sx={{
                    backgroundColor: 'var(--acer-green)',
                    borderRadius: '100%',
                    minWidth: '59px',
                    height: '100%',
                    '&:hover': {
                      backgroundColor: 'var(--acer-green)',
                    },
                  }}
                  onClick={handleSendMessage}
                >
                  <SendOutlined sx={{ color: 'white' }} />
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Chat;
